import { ReactNode } from 'react';
import styled from '@emotion/styled';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';
import { FORM_COLOR as C } from '@/pages/Seminar/components/form/formStyles';
import HomepagePhotoField from './HomepagePhotoField';
import { homepageKey } from './homepagePhotoRules';
import type { HomepagePhotoController } from './useHomepagePhotoAuto';

/**
 * 치과 홈페이지 + 치과 이미지 — 가입 4단계·치과 정보 수정이 같은 순서·모양으로 쓴다(2026-10-30).
 * 순서: 홈페이지 칸(입력창 안 오른쪽 끝 [불러오기]) → 안내 "홈페이지를 입력하면 이미지가 자동 등록됩니다." → 읽기 실패 안내 →
 * (수정 화면) 찾은 값 상자 → 사진 칸. 입력칸 자체는 각 화면 폼 부품을 받는다(가입·수정 입력 모양이 다르다).
 * 칸에서 벗어나면 읽고(같은 주소는 다시 읽지 않는다), [불러오기]는 같은 주소도 다시 읽는다(이미 받은 사진은 다시 받지 않는다).
 */
interface ClinicHomepagePhotoSectionProps {
    photo: HomepagePhotoController;
    /** 칸 이름(각 화면 Label) */
    label: ReactNode;
    /** 홈페이지 입력칸(각 화면 폼 부품 — 칸에서 벗어날 때 photo.readHomepage 를 부른다) */
    input: ReactNode;
    /** 지금 홈페이지 칸 값 — 비었거나 형식이 틀리면 [불러오기] 잠김 */
    url: string;
    /** [불러오기] — 화면이 찾은 값 상자 열쇠 등을 맞춘 뒤 photo.readHomepage(url, { force: true }) */
    onReload: () => void;
    /** 홈페이지 칸 오류 */
    error?: ReactNode;
    /** 안내 아래 — 수정 화면의 찾은 값 상자 */
    extra?: ReactNode;
    /** 대표 사진 칸 오류(가입 필수 확인) */
    photoHasError?: boolean;
    photoError?: ReactNode;
}

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
`;

/** 입력창 안 오른쪽 끝 버튼 자리 — 버튼 폭만큼 입력 글자 오른쪽을 비운다 */
const InputWithAction = styled.div`
    position: relative;

    input {
        padding-right: 104px;
    }
`;

/** [불러오기] — 높이 36·테두리 #D3DBD8·반경 10(같은 화면 보조 버튼과 같은 모양), 입력창 세로 가운데 */
const ReloadButton = styled.button`
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    min-width: 84px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    border: 1px solid ${C.border};
    border-radius: 10px;
    background: #fff;
    color: ${C.primaryDeep};
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

/** 안내·실패 문구·찾은 값 — 아래 사진 칸과 칸 사이 간격(22px)만큼 띄운다 */
const Notes = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 8px 0 22px;
`;

const Hint = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${C.sub};
    word-break: keep-all;
`;

const ClinicHomepagePhotoSection = ({ photo, label, input, url, onReload, error, extra, photoHasError = false, photoError }: ClinicHomepagePhotoSectionProps) => (
    <>
        <Field>
            {label}
            <InputWithAction>
                {input}
                <ReloadButton
                    type="button"
                    // 누를 때 입력칸 포커스가 먼저 빠지면(칸에서 벗어나 읽기 시작) 버튼이 잠겨 눌리지 않는다
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={onReload}
                    disabled={photo.loading || !homepageKey(url)}
                >
                    {photo.loading ? <ButtonSpinner /> : '불러오기'}
                </ReloadButton>
            </InputWithAction>
            {error}
        </Field>
        <Notes>
            <Hint>홈페이지를 입력하면 이미지가 자동 등록됩니다.</Hint>
            {/* 사진 칸 안 안내(fieldMessage)와 같은 문구면 한 번만 */}
            {photo.message && photo.message !== photo.fieldMessage && <Hint role="status">{photo.message}</Hint>}
            {extra}
        </Notes>
        <HomepagePhotoField photo={photo} hasError={photoHasError} />
        {photoError}
    </>
);

export default ClinicHomepagePhotoSection;

/** 홈페이지에서 찾은 주소 — 주소 칸 바로 위(등록 주소와 같으면 부르는 쪽이 넘기지 않는다) */
const AddressBox = styled.div`
    padding: 12px;
    border-radius: 12px;
    background: ${C.primaryCard};
    font-size: 13px;
    line-height: 1.5;
    color: ${C.sub};
    /* 어절 단위 줄바꿈(긴 영문 주소만 칸 안에서 끊는다) */
    word-break: keep-all;
    overflow-wrap: anywhere;

    b {
        color: ${C.text};
    }
`;

export const HomepageAddressHint = ({ address }: { address: string }) => (
    <AddressBox aria-label="홈페이지에서 찾은 주소">
        <b>{address}</b> — 아래 주소 칸에서 이 주소로 검색해 주세요.
    </AddressBox>
);

/** 찾은 주소가 등록 주소와 같은가 — 등록 주소에 찾은 주소 끝 두 마디(도로명·번지)가 들어 있으면 같은 주소 */
export const isSameAddress = (found: string, registered: string | null | undefined): boolean =>
    !!registered && registered.includes(found.split(' ').slice(-2).join(' '));
